
/*
 * Safety rendering shared by the AI panel and the taxon diagnostic panel.
 * Moved here unchanged from AiIdentifyPanel.js so the two cannot drift: the
 * server computes every flag (routes/taxon_knowledge.py) and these components
 * only show them, worst severity first.
 */
export const SEVERITY_RANK = { mild: 1, moderate: 2, severe: 3, deadly: 4 };
export const SEVERITY_STYLE = {
  deadly: { background: 'var(--c-danger-6)', color: 'var(--c-on-accent)', borderColor: 'var(--c-danger-6)' },
  severe: { background: 'var(--c-danger-soft-2)', color: 'var(--c-danger-4-fg)', borderColor: 'var(--c-danger-border-3)' },
  moderate: { background: 'var(--c-warning-soft-5)', color: 'var(--c-warning-text-5-fg)', borderColor: 'var(--c-warning-2)' },
  mild: { background: 'var(--c-warning-soft-6)', color: 'var(--c-warning-text-6-fg)', borderColor: 'var(--c-warning-3)' },
};

export function worst(severities) {
  return severities.reduce(
    (best, s) => (s && (!best || SEVERITY_RANK[s] > SEVERITY_RANK[best]) ? s : best),
    null);
}

/**
 * The response-level safety block. Identifications stored before migration
 * 0014 have none; their per-candidate flag is honoured, so re-opening an old
 * result can never show LESS warning than it once did.
 */
export function safetyOf(result) {
  if (result?.safety) return result.safety;
  const flagged = (result?.predictions || []).filter(p => p.toxicity_flag);
  return {
    toxicity_flag: flagged.length > 0,
    max_severity: null,
    high_risk: flagged.length > 0,
    flagged_candidates: flagged.map(p => p.taxon_name),
    tail: [],
    unverified: flagged.length > 0,
  };
}

/** Flags that differ only in which member species triggered them are one
 * line to a reader: "Amatoxins · Deadly — contains A. phalloides, A. virosa". */
function groupFlags(flags) {
  const groups = new Map();
  (flags || []).forEach((f) => {
    const key = `${f.toxin_class}|${f.severity}|${f.basis}`;
    const g = groups.get(key) || { ...f, taxa: [], sourceIds: new Set() };
    if (!g.taxa.includes(f.taxon)) g.taxa.push(f.taxon);
    if (f.source_id) g.sourceIds.add(f.source_id);
    groups.set(key, g);
  });
  return [...groups.values()].sort(
    (a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
}

export function SafetyBanner({ safety, t }) {
  const tail = safety.tail || [];
  if (!safety.toxicity_flag && !safety.high_risk) return null;
  const severity = safety.max_severity
    || worst(tail.map(x => x.max_severity))
    || (safety.high_risk ? 'severe' : null);
  const palette = SEVERITY_STYLE[severity] || SEVERITY_STYLE.severe;
  return (
    <div role="alert" data-testid="ai-safety-banner" style={{ ...styles.banner, ...palette }}>
      {severity && <strong style={styles.bannerTitle}>{t(`safety.banner.${severity}`)}</strong>}
      <span>{t('toxicWarning')}</span>
      {!safety.toxicity_flag && safety.high_risk && <span>{t('safety.lookalikeRisk')}</span>}
      {tail.length > 0 && (
        <span data-testid="ai-safety-tail">
          {t('safety.tail', { taxa: tail.map(x => x.taxon).join(', ') })}
        </span>
      )}
      {safety.unverified && <span style={styles.bannerNote}>{t('safety.unverified')}</span>}
    </div>
  );
}

function Chip({ severity, children, testId }) {
  const palette = SEVERITY_STYLE[severity] || SEVERITY_STYLE.severe;
  return (
    <div data-testid={testId} style={{ ...styles.chip, ...palette }}>{children}</div>
  );
}

export function CandidateSafety({ p, t }) {
  const s = p.safety;
  if (!s) {
    return p.toxicity_flag
      ? <Chip severity="severe" testId="ai-candidate-flag">{t('safety.flagged')}</Chip>
      : null;
  }
  const flags = groupFlags(s.flags);
  const sameGenus = s.same_genus || [];
  const lookalikes = s.lookalike_of || [];
  if (!flags.length && !sameGenus.length && !lookalikes.length) return null;

  const names = list => [...new Set(list.map(f => f.taxon))].join(', ');
  return (
    <div style={styles.safetyBlock}>
      {flags.map(g => (
        <Chip key={`${g.toxin_class}|${g.severity}|${g.basis}`} severity={g.severity}
              testId="ai-candidate-flag">
          <strong>{t(`safety.toxin.${g.toxin_class}`)} · {t(`safety.severity.${g.severity}`)}</strong>
          {g.basis !== 'direct' && (
            <span> — {t(`safety.basis.${g.basis}`, { taxa: g.taxa.join(', ') })}</span>
          )}
        </Chip>
      ))}
      {sameGenus.length > 0 && (
        <Chip severity={worst(sameGenus.map(f => f.severity))} testId="ai-candidate-same-genus">
          {t('safety.sameGenus', { taxa: names(sameGenus) })}
        </Chip>
      )}
      {lookalikes.length > 0 && (
        <Chip severity={worst(lookalikes.map(f => f.severity))} testId="ai-candidate-lookalike">
          {t('safety.lookalikeOf', { taxa: names(lookalikes) })}
        </Chip>
      )}
    </div>
  );
}

const styles = {
  banner: {
    margin: 0, padding: '10px 12px', borderRadius: 8, border: '1px solid',
    fontSize: 13, lineHeight: 1.45, display: 'flex', flexDirection: 'column', gap: 4,
  },
  bannerTitle: { fontSize: 14 },
  bannerNote: { fontSize: 12, opacity: 0.9 },
  safetyBlock: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 },
  chip: {
    fontSize: 12, lineHeight: 1.4, padding: '5px 8px', borderRadius: 6,
    border: '1px solid',
  },
};
